// EventDrawer — right-side slide-in panel that shows a single event's key
// info + primary contextual action. Opens on row/cell/card click in
// List/Calendar/Kanban without taking over the workspace.
//
// What goes here is the 80% case: ops scans → peeks → sees status,
// drift, flyer, talent, push/announce buttons, audit pointer.
// Anything heavier (Universe field-level overrides, audit dive,
// canonical edits like tier prices) is one click further — "Open full
// editor →" routes to the existing EventDetail page; "Customize for
// Universe →" routes to the focused UniverseOverrides page.

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { Link, useNavigate } from 'react-router-dom';
import {
  DriftDot, LifecycleBadge, formatDateLong,
} from '../lib/events.jsx';
import { formatClock, formatStamp } from '../lib/timeFields.jsx';
import { PLATFORMS, platformMeta, PUB_STATUS_TONE } from '../lib/platforms.js';
import { useVenueTz } from '@/lib/venueTz.js';

export default function EventDrawer({ event, tenantSlug, onClose, onAction }) {
  // Always-fresh fetch — the list query has summary fields but not
  // last_pushed_at per platform, override count, etc. Drawer pulls the
  // detail on open so its data is more current than the list snapshot.
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!event?.id) return;
    let cancelled = false;
    apiFetch(`/api/tenants/${tenantSlug}/events/${event.id}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [event?.id, tenantSlug]);

  const panelRef = useRef(null);
  const closeRef = useRef(null);

  // Esc to close + focus management: move focus into the drawer on open,
  // trap Tab inside it (so it can't escape to the list behind), restore focus
  // to the previously-focused element on close.
  useEffect(() => {
    const prevFocus = document.activeElement;
    closeRef.current?.focus();
    const onKey = (ev) => {
      if (ev.key === 'Escape' && ev.target.tagName !== 'INPUT' && ev.target.tagName !== 'TEXTAREA') {
        onClose();
        return;
      }
      if (ev.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
        else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    };
  }, [onClose]);

  const e = detail || event;
  const navigate = useNavigate();

  const post = async (path) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${e.id}/${path}?authorize_writes=1`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'action_failed');
      if (onAction) onAction();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Backdrop, click-through capture but no auto-close. Click-outside
          dismiss too easy to trigger by accident during an edit. */}
      <div className="fixed inset-0 bg-background/40 z-40 pointer-events-none" />

      <aside
        ref={panelRef}
        className="fixed top-0 right-0 bottom-0 w-[480px] max-w-full bg-background border-l border-border shadow-2xl z-50 flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={`Event ${e.talent || 'details'}`}
      >
        <header className="border-b border-border px-4 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">{formatDateLong(e.date)}</div>
            <h2 className="text-lg font-semibold truncate mt-0.5">{e.talent || 'TBD'}</h2>
            <div className="flex items-center gap-2 mt-1">
              <LifecycleBadge status={e.lifecycle_status || e.status} />
              <DriftDot event={e} />
              {e.day_of_week && <span className="text-xs text-muted-foreground">{e.day_of_week}</span>}
            </div>
          </div>
          <button
            ref={closeRef}
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl leading-none px-1"
            title="Close (Esc)"
            aria-label="Close"
          >✕</button>
        </header>

        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-4 text-sm">
          {error && <div className="text-xs text-destructive">{error}</div>}

          {/* Flyer + key fields */}
          <div className="flex gap-3">
            {e.flyer_image_url ? (
              <img src={e.flyer_image_url} alt={`Flyer, ${e.talent || 'event'}`} className="w-24 h-24 rounded object-cover bg-muted flex-shrink-0" />
            ) : (
              <div className="w-24 h-24 rounded bg-muted text-xs text-muted-foreground flex items-center justify-center flex-shrink-0">no flyer</div>
            )}
            <div className="flex-1 space-y-1 text-xs">
              {e.lineup && <div><span className="text-muted-foreground">Lineup:</span> {e.lineup}</div>}
              {e.doors_at && <div><span className="text-muted-foreground">Doors:</span> {formatClock(e.doors_at, e.venue_timezone)}</div>}
              {e.start_at && <div><span className="text-muted-foreground">Show:</span> {formatClock(e.start_at, e.venue_timezone)}</div>}
              {e.end_at && <div><span className="text-muted-foreground">End:</span> {formatClock(e.end_at, e.venue_timezone)}</div>}
              {e.age_limit && <div><span className="text-muted-foreground">Age:</span> {e.age_limit}+</div>}
              {e.ref_code && <div><span className="text-muted-foreground">Ref code:</span> <code>{e.ref_code}</code></div>}
            </div>
          </div>

          {/* Per-platform rows, replaces the old single PublicationRollup
              + single "Customize for Universe" link. Driven by the
              src/lib/platforms.js registry; new platform = new row
              automatically once a publication exists. */}
          <div>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wide mb-1.5">Platforms</div>
            <div className="border border-border rounded-md divide-y divide-border">
              {(e.publications || []).length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground italic">Not on any platform yet. Use “Push placeholder” or “Announce” below to push it to all platforms.</div>
              ) : (
                e.publications.map((pub) => (
                  <PlatformRow
                    key={pub.platform}
                    pub={pub}
                    event={e}
                    tenantSlug={tenantSlug}
                  />
                ))
              )}
            </div>
          </div>

          {/* Meta, attribution/insights, not a publish target. Campaigns
              are created in Ads Manager + matched to the event by name, so
              Meta gets a read-only matched-campaign summary instead of a
              PlatformRow (no event_publications row, no override editor). */}
          <MetaInsightsRow tenantSlug={tenantSlug} eventId={e.id} />

          {/* Primary contextual action, multi-platform, stays prominent
              below the per-platform list. Push placeholder / Announce /
              Sync / Check drift depending on event state. */}
          <PrimaryAction event={e} busy={busy} onPost={post} onNavigate={navigate} tenantSlug={tenantSlug} />

          {/* Linkouts, full editor + audit history. The per-platform
              "Configure →" lives on each PlatformRow above. */}
          <div className="border-t border-border pt-3 space-y-1.5 text-xs">
            <Link
              to={`/${tenantSlug}/event/${e.id}`}
              className="block text-primary hover:underline"
            >Open full editor →</Link>
            {Array.isArray(e.audit_log) && (
              <Link
                to={`/${tenantSlug}/event/${e.id}#audit`}
                className="block text-muted-foreground hover:underline"
              >Audit history ({e.audit_log.length} change{e.audit_log.length === 1 ? '' : 's'}) →</Link>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

// Meta matched-campaign summary. Lazy-loads /meta-insights (the existing
// event-scoped endpoint that matches campaigns by date-in-name). Read-only
// — surfaces matched count + spend + a link to the full Meta panel on the
// event page. Silent when no Meta creds / no matched campaigns.
function MetaInsightsRow({ tenantSlug, eventId }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/meta-insights`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setHidden(true); });   // creds missing / error → hide
    return () => { cancelled = true; };
  }, [tenantSlug, eventId]);

  if (hidden) return null;
  if (!data) {
    return <div className="text-[10px] text-muted-foreground">Meta: checking matched campaigns…</div>;
  }
  if (!data.matched_count) {
    return (
      <div className="text-xs flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-muted text-[10px] font-mono">M</span>
        <span className="text-muted-foreground">Meta · no matched campaigns</span>
      </div>
    );
  }
  // /meta-insights totals carry spend_cents (always cents). Divide by 100.
  const spendCents = data.totals?.spend_cents ?? 0;
  const spendStr = `$${Math.round(spendCents / 100).toLocaleString()}`;
  return (
    <div className="border border-border rounded-md p-2.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-muted text-[10px] font-mono">M</span>
        <span className="font-medium flex-1">Meta</span>
        <span className="text-muted-foreground">{data.matched_count} campaign{data.matched_count === 1 ? '' : 's'} · {spendStr}</span>
      </div>
      <div className="mt-1.5 text-right">
        <Link
          to={`/${tenantSlug}/event/${eventId}#meta`}
          className="text-[10px] text-primary hover:underline"
        >View performance →</Link>
      </div>
    </div>
  );
}

// One row per platform tied to the event. Reads platform UI metadata
// from the registry (PLATFORMS) — adding a new platform = registering it
// in src/lib/platforms.js + writing its editor page. Drawer code stays
// the same. Each row shows: short tag · platform name · sync status ·
// drift dot (where supported) · override count · Configure → link ·
// Open in {Platform} ↗ external link.
function PlatformRow({ pub, event, tenantSlug }) {
  const tz = useVenueTz();
  const meta = platformMeta(pub.platform);
  const overridesKey = `${pub.platform}_overrides`;
  const overrideCount = Object.keys(event?.[overridesKey] || {}).length;
  const externalUrl = meta.externalUrlField ? pub[meta.externalUrlField] : null;
  const tone = PUB_STATUS_TONE[pub.status] || PUB_STATUS_TONE.pending;

  // Per-platform drift: drift dot here would need per-platform has_drift
  // on the event. For now we fall back to the aggregate event.has_drift
  // dot only on platforms registered with hasDrift.
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-xs">
      <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-muted text-[10px] font-mono">{meta.short}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium truncate">{meta.label}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${tone.cls}`}>{tone.label}</span>
          {meta.hasDrift && event?.has_drift && <DriftDot event={event} size={6} />}
        </div>
        {pub.last_synced_at && (
          <div className="text-[10px] text-muted-foreground">
            last synced {formatStamp(pub.last_synced_at, tz)}
            {meta.hasOverrides && overrideCount > 0 && ` · ${overrideCount} custom value${overrideCount === 1 ? '' : 's'}`}
          </div>
        )}
        {pub.sync_error && (
          <div className="text-[10px] text-destructive mt-0.5 break-words" title={String(pub.sync_error)}>
            ⚠ {String(pub.sync_error)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {meta.hasOverrides && meta.editorPath && (
          <Link
            to={`/${tenantSlug}/event/${event.id}/${meta.editorPath}`}
            className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent"
          >Configure →</Link>
        )}
        {externalUrl && (
          <a
            href={externalUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent"
            title={`Open in ${meta.label}`}
          >Open ↗</a>
        )}
      </div>
    </div>
  );
}

// Picks ONE primary action by event state, matches the RowActions
// pattern but with full-width drawer button styling.
function PrimaryAction({ event, busy, onPost, onNavigate, tenantSlug }) {
  const hasTalent = !!event.talent && event.talent !== 'TBD' && !/^(tbd|tba|placeholder|artist\s*\d+)$/i.test(String(event.talent).trim());
  const status = event.lifecycle_status || event.status;
  const drift = event.has_drift === true;

  if (status === 'cancelled' || status === 'past') return null;

  if (drift) {
    return (
      <button
        onClick={() => onNavigate(`/${tenantSlug}/event/${event.id}/universe`)}
        className="w-full px-3 py-2 text-sm border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-500/20"
      >Check drift → Compare and resolve</button>
    );
  }

  if ((status === 'sized_up' || status === 'pending_announce') && hasTalent) {
    return (
      <button
        onClick={() => onPost('announce')}
        disabled={busy}
        className="w-full px-3 py-2 text-sm bg-emerald-600 text-white rounded hover:opacity-90 disabled:opacity-50"
      >{busy ? 'Announcing…' : 'Announce + sync (sets Live)'}</button>
    );
  }

  if (status === 'draft' || status === 'sized_up' || status === 'pending_announce') {
    return (
      <button
        onClick={() => onPost('sync-platforms')}
        disabled={busy}
        className="w-full px-3 py-2 text-sm border border-input rounded hover:bg-accent disabled:opacity-50"
      >{busy ? 'Pushing…' : '+ Push placeholder to platforms'}</button>
    );
  }

  if (status === 'announced') {
    return (
      <button
        onClick={() => onPost('sync-platforms')}
        disabled={busy}
        className="w-full px-3 py-2 text-sm border border-input rounded hover:bg-accent disabled:opacity-50"
      >{busy ? 'Syncing…' : 'Sync to platforms (no status change)'}</button>
    );
  }

  return null;
}
